// ============================================================================
// A TRANSCRIPT AS A FILE — what "Download transcript" writes (owner, 2026-09-17).
//
// WHY A FILE AT ALL. The transcripts live in one place, behind a login-only API, and the owner
// wants them in other tools — a claude.ai Project, mostly. On 2026-09-12 that was a one-off
// export script run by hand; this is the same document made from inside the app, one lecture at
// a time, so a new lecture is one click from wherever he reads it.
//
// THE FORMAT IS THE EXPORT'S FORMAT, on purpose: a header a reader (or a model) can trust —
// course, date, length, where the words came from and how sure to be of the speaker labels —
// then the transcript as timestamped paragraphs. Markdown, because it is plain text that every
// destination reads and nothing has to be installed to open it.
//
// NOTHING HERE FAKES KNOWLEDGE. A lecture with no title is headed by its date and course, not
// by an invented name. A segment with no speaker gets no speaker. A speaker label is stated as
// what it is: a likelihood from talk time (see the Transcript type's `speakers` comment).
// ============================================================================

import type { TranscriptSegment } from '@app/shared'

import type { LectureTranscript } from './data'

/** `'1:02:07'` or `'07:30'` — hours only when there are any. */
export function clockAt(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/**
 * Consecutive segments become one paragraph while the speaker is the same and the gap between
 * them is under ninety seconds. A paragraph carries the clock of its first segment. Whisper's
 * segments are sentence-sized; a page of one-line paragraphs each with its own timestamp reads
 * like a log, not a lecture.
 */
const PARAGRAPH_GAP_MS = 90_000

interface Paragraph {
  startMs: number
  speaker: string | null
  text: string[]
}

export function paragraphs(segments: readonly TranscriptSegment[]): Paragraph[] {
  const out: Paragraph[] = []
  let open: Paragraph | null = null
  let lastEnd = 0
  for (const seg of segments) {
    const text = seg.text.trim()
    if (!text) continue
    const speaker = seg.speaker ?? null
    if (!open || open.speaker !== speaker || seg.startMs - lastEnd > PARAGRAPH_GAP_MS) {
      open = { startMs: seg.startMs, speaker, text: [] }
      out.push(open)
    }
    open.text.push(text)
    lastEnd = seg.endMs
  }
  return out
}

/** The whole document. */
export function transcriptMarkdown(detail: LectureTranscript): string {
  const { lecture, transcript, segments } = detail
  const course = lecture.courseCode
  const title = lecture.title ?? `Lecture on ${lecture.date}`
  const minutes = lecture.durationSec !== null ? Math.round(lecture.durationSec / 60) : null
  const labelled = segments.some((s) => s.speaker)

  const lines: string[] = []
  lines.push(`# ${course} — ${title}`)
  lines.push('')
  lines.push(`- Date: ${lecture.date}`)
  if (minutes !== null) lines.push(`- Length: ${minutes} min`)
  lines.push(`- Words: ${transcript.wordCount.toLocaleString()}`)
  lines.push(
    `- Source: LifeOS lecture transcript (Whisper on the owner's PC)` +
      (labelled ? '; speaker labels are a likelihood from talk time, not a verified identity' : ''),
  )
  lines.push(`- Lecture id: ${lecture.id}`)
  lines.push('')
  lines.push('## Transcript')
  lines.push('')
  for (const p of paragraphs(segments)) {
    const who = p.speaker ? ` ${p.speaker}:` : ''
    lines.push(`**[${clockAt(p.startMs)}]${who}** ${p.text.join(' ')}`)
    lines.push('')
  }
  return lines.join('\n')
}

/**
 * `'2026-09-11 · NEU C61 · Neu c61 Sep 11.md'` — sorts by date in a folder, says the course,
 * keeps the recording's own name. Characters a filesystem refuses are dropped, not replaced with
 * something that looks like part of the title.
 */
export function transcriptFilename(detail: LectureTranscript): string {
  const { lecture } = detail
  const safe = (s: string) =>
    s
      .replace(/[<>:"/\\|?*\x00-\x1f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80)
  const name = lecture.title ? safe(lecture.title.replace(/\.[a-z0-9]{2,4}$/i, '')) : 'Lecture'
  return `${lecture.date} · ${safe(lecture.courseCode)} · ${name}.md`
}

/**
 * Hand the browser a file to save. A Blob URL on a temporary anchor is the one download path
 * that needs no server round-trip and no permission prompt; the browser puts it wherever it puts
 * downloads. The URL is revoked on the next tick, after the click has been dispatched.
 */
export function saveTextFile(filename: string, text: string, mime = 'text/markdown'): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
